<script setup lang="ts">
import { BellOutlined } from '@ant-design/icons-vue'
import { ConfigProvider as AConfigProvider } from 'ant-design-vue'
import darkAlgorithm from 'ant-design-vue/es/theme/themes/dark'
import defaultAlgorithm from 'ant-design-vue/es/theme/themes/default'
import { computed, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { fetchActiveSiteAnnouncements } from '@/services/announcements'
import { useAuthStore } from '@/stores/auth'
import { useThemeStore } from '@/stores/theme'

const themeStore = useThemeStore()
const auth = useAuthStore()
const router = useRouter()
themeStore.init()

const themeConfig = computed(() => ({
  algorithm: themeStore.isDark ? darkAlgorithm : defaultAlgorithm,
}))

const showNotificationBanner = ref(false)
const siteAnnouncements = ref<
  Array<{ id: string; title: string; content: string; background_color?: string }>
>([])

const checkNotificationBanner = () => {
  if (!auth.isAuthenticated) {
    showNotificationBanner.value = false
    return
  }
  if ('Notification' in window) {
    try {
      if (Notification.permission === 'default') {
        const dismissed = sessionStorage.getItem('notif_banner_dismissed')
        if (!dismissed) showNotificationBanner.value = true
        else showNotificationBanner.value = false
      } else {
        showNotificationBanner.value = false
      }
    } catch {
      showNotificationBanner.value = false
    }
  }
}

onMounted(async () => {
  checkNotificationBanner()
  try {
    siteAnnouncements.value = await fetchActiveSiteAnnouncements()
  } catch {
    siteAnnouncements.value = []
  }
})

watch(() => auth.isAuthenticated, checkNotificationBanner)

const goToNotifications = () => {
  showNotificationBanner.value = false
  sessionStorage.setItem('notif_banner_dismissed', '1')
  router.push('/settings/notifications')
}

const dismissBanner = () => {
  showNotificationBanner.value = false
  sessionStorage.setItem('notif_banner_dismissed', '1')
}
</script>

<template>
  <a-config-provider :theme="themeConfig">
    <a-alert
      v-for="a in siteAnnouncements"
      :key="a.id"
      type="warning"
      banner
      closable
      :message="a.title"
      :description="a.content"
      :style="{ backgroundColor: a.background_color || undefined }"
    />
    <a-alert
      v-if="showNotificationBanner"
      type="info"
      banner
      closable
      @close="dismissBanner"
    >
      <template #message>
        <BellOutlined style="margin-right: 8px" />
        Enable notifications to get instant alerts for new bookings, payments, and messages.
        <a-button type="link" size="small" style="padding: 0 4px; height: auto" @click="goToNotifications">
          Open Settings
        </a-button>
      </template>
    </a-alert>
    <router-view />
  </a-config-provider>
</template>

<style>
:root {
  --text-secondary: rgba(0, 0, 0, 0.45);
  --text-muted: #999;
  --bg-container: #ffffff;
  --bg-layout: #f5f5f5;
  --bg-component: #fafafa;
  --border-color: #f0f0f0;
}

[data-theme='dark'] {
  --text-secondary: rgba(255, 255, 255, 0.45);
  --text-muted: rgba(255, 255, 255, 0.35);
  --bg-container: #141414;
  --bg-layout: #000000;
  --bg-component: #1f1f1f;
  --border-color: #303030;
}
</style>
